import type { IFinancialsTemplateFieldsFragment } from "components/financials/modern/graphql/fragments/__generated__/FinancialsTemplateFieldsFragment.generated";
import useTemplates from "components/financials/modern/templates/hooks/useTemplates";
import TemplateSheetContentForm from "components/financials/modern/templates/sheet/TemplateSheetContentForm";
import TemplateSheetContentView from "components/financials/modern/templates/sheet/TemplateSheetContentView";
import { TemplateSheetHeader } from "components/financials/modern/templates/sheet/TemplateSheetHeader";
import TemplateSheetTrigger from "components/financials/modern/templates/sheet/TemplateSheetTrigger";
import { TemplatesEnum } from "components/financials/modern/templates/types";
import type { TemplatesWrapperProps } from "components/financials/modern/templates/TemplatesWrapper";
import classNames from "components/ui/classNames";
import { Sheet, SheetContent } from "components/ui/Sheet";
import { useMemo, useState } from "react";
import {
  IFinancialsTemplateNamespace,
  IFinancialsTemplatesQueryOrderField,
  IOrderDirection,
  IPlatformVertical,
} from "types/graphqlTypes";

export default function Templates({
  vertical,
  indicatorDataSource,
  financialsTemplate,
  systemTemplate,
  overridingTemplate,
  renderingTemplate,
  onChange,
  onRefetchFinancialsTemplate,
}: TemplatesWrapperProps) {
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [isCreatingTemplate, setIsCreatingTemplate] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<IFinancialsTemplateFieldsFragment | null>(null);
  const [selectedTab, setSelectedTab] = useState<TemplatesEnum>(TemplatesEnum.MyTemplates);

  const initialVariables = useMemo(
    () => ({
      filters: {
        filterByCountry: null,
        filterByIndicatorDataSource: indicatorDataSource,
        filterByTemplateCodes: systemTemplate?.templateCodes || null,
        filterByPlatformVertical: [vertical, IPlatformVertical.All],
        filterByViewerArchivedTemplates: null,
        filterByViewerId: null,
        // Keep system templates hidden
        filterByNamespace: [IFinancialsTemplateNamespace.Fundamentei, IFinancialsTemplateNamespace.User],
      },
      orderBy: {
        field: IFinancialsTemplatesQueryOrderField.UseCount,
        direction: IOrderDirection.Descending,
      },
      limit: 20,
      cursor: null,
    }),
    [vertical, indicatorDataSource, systemTemplate?.templateCodes],
  );

  const { ownTemplates, ourTemplates, publicTemplates, starredTemplates } = useTemplates(initialVariables);

  const isDefiningTemplate = isCreatingTemplate || editingTemplate !== null;

  const viewerCanResetToOriginal =
    (systemTemplate &&
      overridingTemplate &&
      renderingTemplate &&
      overridingTemplate.objectId !== systemTemplate.objectId &&
      renderingTemplate.objectId === overridingTemplate.objectId) ||
    (systemTemplate &&
      renderingTemplate &&
      renderingTemplate.overrideCriteria &&
      renderingTemplate.objectId !== systemTemplate.objectId);
  const viewerCanPause = systemTemplate && renderingTemplate && systemTemplate.objectId !== renderingTemplate.objectId;
  const isSystemTemplateActive = !!renderingTemplate && renderingTemplate.objectId !== systemTemplate?.objectId;

  return (
    <Sheet
      modal={true}
      open={isSheetOpen}
      onOpenChange={() => {
        setIsSheetOpen(false);
        setIsCreatingTemplate(false);
        setEditingTemplate(null);
      }}
    >
      <TemplateSheetTrigger
        ownTemplates={ownTemplates}
        ourTemplates={ourTemplates}
        starredTemplates={starredTemplates}
        systemTemplate={systemTemplate}
        renderingTemplate={renderingTemplate}
        isSheetOpen={isSheetOpen}
        isSystemTemplateActive={isSystemTemplateActive}
        viewerCanResetToOriginal={!!viewerCanResetToOriginal}
        viewerCanPause={!!viewerCanPause}
        setIsSheetOpen={setIsSheetOpen}
        setSelectedTab={setSelectedTab}
        onRefetchFinancialsTemplate={onRefetchFinancialsTemplate}
        onChange={onChange}
      />

      <SheetContent
        position="right"
        className={classNames(
          "h-dvh w-screen space-y-6 overflow-y-auto border-l border-slate-300 bg-white p-4 pt-8 outline-none",
          "sm:p-10",
          "xl:w-3/6",
          "dark:border-slate-800 dark:bg-slate-950",
        )}
        onClick={(event) => event.stopPropagation()}
        onEscapeKeyDown={(event) => event.preventDefault()}
      >
        <TemplateSheetHeader
          isCreatingTemplate={isCreatingTemplate}
          isDefiningTemplate={isDefiningTemplate}
          isEditingTemplate={!!editingTemplate}
          vertical={vertical}
          loading={ownTemplates.loading}
          onRefresh={() => {
            ownTemplates.refetch({});
            publicTemplates.refetch({});
            starredTemplates.refetch({});
          }}
        />

        <TemplateSheetContentView
          isDefiningTemplate={isDefiningTemplate}
          selectedTab={selectedTab}
          isSheetOpen={isSheetOpen}
          initialVariables={initialVariables}
          systemTemplate={systemTemplate}
          renderingTemplate={renderingTemplate}
          financialsTemplate={financialsTemplate}
          ownTemplates={ownTemplates}
          publicTemplates={publicTemplates}
          starredTemplates={starredTemplates}
          setIsCreatingTemplate={setIsCreatingTemplate}
          setSelectedTab={setSelectedTab}
          setEditingTemplate={setEditingTemplate}
          setIsSheetOpen={setIsSheetOpen}
          onChange={onChange}
          onRefetchFinancialsTemplate={onRefetchFinancialsTemplate}
        />

        <TemplateSheetContentForm
          isCreatingTemplate={isCreatingTemplate}
          editingTemplate={editingTemplate}
          indicatorDataSource={indicatorDataSource}
          financialsTemplate={financialsTemplate}
          onCancel={() => setEditingTemplate(null)}
          onCompleted={() => ownTemplates.refetch()}
          setEditingTemplate={setEditingTemplate}
          setIsCreatingTemplate={setIsCreatingTemplate}
        />
      </SheetContent>
    </Sheet>
  );
}
